/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Camera, useThree } from '@react-three/fiber';
import clone from 'lodash/clone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { EDraggablePointType, EInteractionMode, EWall } from '../@types/Enums';
import { IBoundaryEdge } from '../models/abstract/BoundaryEdge';
import { IWall } from '../models/abstract/Wall';
import PhysicalBoundaryModel, { BoundaryEdge } from '../models/standard/PhysicalBoundary';
import StandardWallModel from '../models/standard/StandardWallModel';
import { useBuilding } from '../provider/BuildingProvider/BuildingProvider';
import { useDraggingContext } from '../provider/DraggingProvider';
import { useMultiSelectToggleContext } from '../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../provider/ToolbarProvider';
import globalEventManager from '../utils/globalEventManager';
import { getSnappedPoint } from '../utils/snappingUtils';

// --- CONSTANTS ---
const OMNI_SNAP_RADIUS = 0.15;
const EPS = 1e-3;
const HEAL_TOLERANCE = 0.05;

// --- HELPERS ---

const isSame2 = (a: THREE.Vector3, b: THREE.Vector3, eps = HEAL_TOLERANCE) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

type EndKind = 'start' | 'end';

function whichEnd(w: IWall, p: THREE.Vector3): EndKind | null {
  if (isSame2(w.startPoint, p)) return 'start';
  if (isSame2(w.endPoint, p)) return 'end';
  return null;
}

export function getConnectedWallsAtPoint(walls: IWall[], point: THREE.Vector3, selfIndex: number): Array<{ index: number; end: EndKind }> {
  const res: Array<{ index: number; end: EndKind }> = [];
  walls.forEach((w, i) => {
    if (i === selfIndex) return;
    if (!(w instanceof StandardWallModel)) return;
    const end = whichEnd(w, point);
    if (end) res.push({ index: i, end });
  });
  return res;
}

function isValidSegmentFromPoints(a: THREE.Vector3, b: THREE.Vector3): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx * dx + dy * dy > EPS * EPS;
}

function setWallEndPreserveRef(w: StandardWallModel, end: EndKind, pos: THREE.Vector3) {
  const p = end === 'start' ? w.startPoint : w.endPoint;
  p.copy(pos);
  p.z = 0;
  if (end === 'start') w.setStartPoint(p);
  else w.setEndPoint(p);
  if (typeof (w as any).updateWallLengths === 'function') (w as any).updateWallLengths();
}

function cloneStandardWallDeep(w: StandardWallModel): StandardWallModel {
  const out = Object.assign(Object.create(Object.getPrototypeOf(w)), w) as StandardWallModel;
  out.startPoint = w.startPoint.clone();
  out.endPoint = w.endPoint.clone();
  out.attributes = w.attributes ? [...w.attributes] : [];
  out.doors = w.doors ? w.doors.map((d) => clone(d)) : [];
  out.windows = w.windows ? w.windows.map((win) => clone(win)) : [];
  if (w.sides) {
    out.sides = {};
    (['A', 'B'] as const).forEach((side) => {
      if (w.sides?.[side]) {
        out.sides![side] = { radiators: w.sides![side]!.radiators.map((r) => clone(r)) };
      }
    });
  }
  return out;
}

// --- CONSOLIDATION LOGIC ---

const clamp01 = (t: number) => Math.max(0, Math.min(1, t));

function wallPointAt(w: IWall, t: number): THREE.Vector3 {
  const a = w.startPoint;
  const b = w.endPoint;
  return new THREE.Vector3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, 0);
}

function projectT(w: IWall, p: THREE.Vector3): number {
  const a = w.startPoint;
  const b = w.endPoint;
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const denom = abx * abx + aby * aby;
  if (denom < EPS * EPS) return 0;
  return ((p.x - a.x) * abx + (p.y - a.y) * aby) / denom;
}

function isLoopContinuous(edges: IBoundaryEdge[], eps = HEAL_TOLERANCE): boolean {
  if (edges.length < 3) return false;
  for (let i = 0; i < edges.length; i++) {
    const cur = edges[i];
    const nxt = edges[(i + 1) % edges.length];

    const curEnd = wallPointAt(cur.wall, cur.reversed ? cur.t0 : cur.t1);
    const nxtStart = wallPointAt(nxt.wall, nxt.reversed ? nxt.t1 : nxt.t0);
    
    if (Math.abs(curEnd.x - nxtStart.x) > eps || Math.abs(curEnd.y - nxtStart.y) > eps) {
      return false;
    }
  }
  return true;
}

function ensureClockwiseEdges(edges: IBoundaryEdge[]): IBoundaryEdge[] {
  if (!edges || edges.length < 3) return edges;

  const out: IBoundaryEdge[] = [];
  const used = new Array(edges.length).fill(false);

  out.push(edges[0]);
  used[0] = true;

  const getRawGeometry = (ed: IBoundaryEdge) => {
    const pt0 = wallPointAt(ed.wall, ed.t0);
    const pt1 = wallPointAt(ed.wall, ed.t1);
    return ed.reversed ? { s: pt1, e: pt0 } : { s: pt0, e: pt1 };
  };

  let lastEnd = getRawGeometry(edges[0]).e;

  for (let count = 1; count < edges.length; count++) {
    let found = false;
    for (let i = 1; i < edges.length; i++) {
      if (used[i]) continue;
      const geom = getRawGeometry(edges[i]);

      if (isSame2(lastEnd, geom.s)) {
        out.push(edges[i]);
        lastEnd = geom.e;
        used[i] = true;
        found = true;
        break;
      } else if (isSame2(lastEnd, geom.e)) {
        const flippedEdge = Object.assign(Object.create(Object.getPrototypeOf(edges[i])), edges[i]);
        flippedEdge.reversed = !flippedEdge.reversed;
        out.push(flippedEdge);
        lastEnd = geom.s;
        used[i] = true;
        found = true;
        break;
      }
    }
    if (!found) {
      // FIX: Niemals Wände wegwerfen! Wenn durch Floating-Point-Lücken 
      // keine exakte Verbindung gefunden wird, zwingen wir die restlichen 
      // Wände trotzdem in den Loop, damit der Raum keine Wände verliert.
      const firstUnused = used.findIndex((u) => !u);
      if (firstUnused !== -1) {
        out.push(edges[firstUnused]);
        lastEnd = getRawGeometry(edges[firstUnused]).e;
        used[firstUnused] = true;
      } else {
        break; 
      }
    }
  }

  const pts = out.map((ed) => getRawGeometry(ed).s);
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    area += (p2.x - p1.x) * (p2.y + p1.y);
  }

  if (area < 0) {
    return out
      .map((ed) => {
        const flipped = Object.assign(Object.create(Object.getPrototypeOf(ed)), ed);
        flipped.reversed = !flipped.reversed;
        return flipped;
      })
      .reverse();
  }

  return out;
}

function rehydrateAllWallRefs(next: IBuildingProviderContext, finalWalls: IWall[], replacements: Map<string, StandardWallModel>): IBuildingProviderContext {
  const byUuid = new Map(finalWalls.map((w) => [w.uuid, w] as const));

  const resolveUuid = (uuid: string): string | null => {
    let cur: string | null = uuid;
    let hops = 0;
    while (cur && replacements.has(cur) && hops++ < 10) cur = replacements.get(cur)!.uuid;
    return cur;
  };

  const getCanonical = (w: IWall | undefined | null): IWall | undefined => {
    if (!w) return undefined;
    const u = resolveUuid(w.uuid) ?? w.uuid;
    return byUuid.get(u); // FIX: "|| w" entfernt, damit gelöschte Wände nicht als Untote zurückkommen
  };

  const globalWallSideUsage = new Map<string, Set<'A' | 'B'>>();
  const spaceWallAssignedSide = new Map<string, 'A' | 'B'>();

  const rooms = (next.rooms ?? []).map((room) => {
    const nextRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);

    nextRoom.spaces = (room.spaces ?? []).map((space) => {
      const nextSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);

      if (Array.isArray(nextSpace.walls)) {
        const uniqueUuids = new Set<string>();
        nextSpace.walls = nextSpace.walls.map(getCanonical).filter((w): w is IWall => {
          if (!w || uniqueUuids.has(w.uuid)) return false;
          uniqueUuids.add(w.uuid);
          return true;
        });
      }

      if (Array.isArray(nextSpace.boundaries)) {
        nextSpace.boundaries = nextSpace.boundaries.map((b) => {
          if (!(b instanceof PhysicalBoundaryModel)) return b;
          const nb: any = Object.assign(Object.create(Object.getPrototypeOf(b)), b);

          if (Array.isArray(nb.edges)) {
            const relinked = (nb.edges as IBoundaryEdge[])
              .map((edge) => {
                if (!edge?.wall) return null;
                const oldWall = edge.wall;
                const newWall = getCanonical(oldWall);

                if (!newWall) return null;

                const ne: any = Object.assign(Object.create(Object.getPrototypeOf(edge)), edge);

                if (newWall !== oldWall) {
                  const d1 = oldWall.endPoint.clone().sub(oldWall.startPoint);
                  const d2 = newWall.endPoint.clone().sub(newWall.startPoint);
                  const isFlipped = d1.dot(d2) < 0;

                  const oldA = wallPointAt(oldWall, edge.t0);
                  const oldB = wallPointAt(oldWall, edge.t1);

                  // FIX: Rein geometrische Projektion
                  let nt0 = clamp01(projectT(newWall, oldA));
                  let nt1 = clamp01(projectT(newWall, oldB));

                  if (nt0 < 1e-4) nt0 = 0;
                  if (nt0 > 1 - 1e-4) nt0 = 1;
                  if (nt1 < 1e-4) nt1 = 0;
                  if (nt1 > 1 - 1e-4) nt1 = 1;

                  if (Math.abs(nt1 - nt0) < 1e-4) return null;

                  if (isFlipped) {
                    const temp = nt0;
                    nt0 = nt1;
                    nt1 = temp;
                    ne.reversed = !ne.reversed;
                  }
                  ne.wall = newWall;
                  ne.t0 = nt0;
                  ne.t1 = nt1;
                }

                const usageKey = `${space.uuid}_${newWall.uuid}`;

                if (spaceWallAssignedSide.has(usageKey)) {
                  ne.side = spaceWallAssignedSide.get(usageKey);
                } else {
                  let desiredSide: 'A' | 'B' = 'A';

                  if (!globalWallSideUsage.has(newWall.uuid)) {
                    globalWallSideUsage.set(newWall.uuid, new Set(['A']));
                  } else {
                    const used = globalWallSideUsage.get(newWall.uuid)!;
                    if (used.has('A') && !used.has('B')) {
                      desiredSide = 'B';
                      used.add('B');
                    } else if (!used.has('A')) {
                      desiredSide = 'A';
                      used.add('A');
                    } else {
                      desiredSide = 'B';
                    }
                  }

                  ne.side = desiredSide;
                  spaceWallAssignedSide.set(usageKey, desiredSide);
                }

                if (ne.resetPoints) ne.resetPoints();
                return ne as IBoundaryEdge;
              })
              .filter(Boolean) as IBoundaryEdge[];

            const seen = new Set<string>();
            const deduped: IBoundaryEdge[] = [];
            for (const e of relinked) {
              const k = `${e.wall.uuid}:${Math.round(e.t0 * 1000)}:${Math.round(e.t1 * 1000)}`;
              if (seen.has(k)) continue;
              seen.add(k);
              deduped.push(e);
            }

            nb.edges = ensureClockwiseEdges(deduped);

            // FIX: Inline Health Check rebuildet defekte Boundaries intelligent und bewahrt A/B-Topologie
            if (nb.edges.length < 3 || !isLoopContinuous(nb.edges)) {
              const validSpaceWalls = (nextSpace.walls ?? []).filter(
                (w): w is StandardWallModel => w instanceof StandardWallModel
              );
              
              if (validSpaceWalls.length >= 3) {
                let freshEdges = validSpaceWalls.map((w) => new BoundaryEdge(w, 0, 1, 'A', false));
                freshEdges = ensureClockwiseEdges(freshEdges);
                
                freshEdges.forEach((ne) => {
                  const usageKey = `${space.uuid}_${ne.wall.uuid}`;
                  if (spaceWallAssignedSide.has(usageKey)) {
                    ne.side = spaceWallAssignedSide.get(usageKey)!;
                  } else {
                    let desiredSide: 'A' | 'B' = 'A';
                    if (!globalWallSideUsage.has(ne.wall.uuid)) {
                      globalWallSideUsage.set(ne.wall.uuid, new Set(['A']));
                    } else {
                      const used = globalWallSideUsage.get(ne.wall.uuid)!;
                      if (used.has('A') && !used.has('B')) {
                        desiredSide = 'B';
                        used.add('B');
                      } else if (!used.has('A')) {
                        desiredSide = 'A';
                        used.add('A');
                      } else {
                        desiredSide = 'B';
                      }
                    }
                    ne.side = desiredSide;
                    spaceWallAssignedSide.set(usageKey, desiredSide);
                  }
                });
                nb.edges = freshEdges;
              }
            }

            const wseen = new Set<string>();
            nb.walls = nb.edges
              .map((e: IBoundaryEdge) => e.wall)
              .filter((w: IWall) => {
                if (!w || wseen.has(w.uuid)) return false;
                wseen.add(w.uuid);
                return true;
              });
          }
          return nb;
        });
      }
      return nextSpace;
    });
    return nextRoom;
  });

  return { ...next, rooms };
}

export function consolidateBuildingWalls(building: IBuildingProviderContext): IBuildingProviderContext {
  const walls = building.walls ? [...building.walls] : [];
  const toDelete = new Set<string>();
  const replacements = new Map<string, StandardWallModel>();
  let anyCloned = false;

  const SAFE_MERGE_EPS = 0.02;
  const SAFE_SNAP_TOL = 0.02;
  const COLLAPSE_EPS = 0.05;

  // =========================================================================
  // 1. ZERO-LENGTH COLLAPSE
  // =========================================================================
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    if (!(w instanceof StandardWallModel)) continue;
    if (w.startPoint.distanceTo(w.endPoint) >= COLLAPSE_EPS) continue;

    // FIX: Einfach restlos löschen, ohne falsche Nachbarn zuzuordnen
    toDelete.add(w.uuid);
  }
  // =========================================================================

  const incumbentWallUuids = new Set<string>();
  (building.rooms ?? []).forEach((r) => {
    (r.spaces ?? []).forEach((s) => {
      (s.walls ?? []).forEach((w) => {
        if (w) incumbentWallUuids.add(w.uuid);
      });
      (s.boundaries ?? []).forEach((b) => {
        if (b instanceof PhysicalBoundaryModel && Array.isArray(b.edges)) {
          b.edges.forEach((e: IBoundaryEdge) => {
            if (e?.wall?.uuid) incumbentWallUuids.add(e.wall.uuid);
          });
        }
      });
    });
  });

  const getWeightedScore = (w: StandardWallModel) => {
    let score = 0;
    if ((w as any).filesApiId) score += 50000;
    if ((w.doors?.length ?? 0) > 0) score += 1000;
    if ((w.windows?.length ?? 0) > 0) score += 1000;
    score += w.startPoint.distanceTo(w.endPoint) * 0.1;
    if (incumbentWallUuids.has(w.uuid)) score += 100000;
    return score;
  };

  const areWallsIdentical = (w1: IWall, w2: IWall) => {
    const s1 = w1.startPoint,
      e1 = w1.endPoint;
    const s2 = w2.startPoint,
      e2 = w2.endPoint;
    const matchDirect = s1.distanceTo(s2) < SAFE_MERGE_EPS && e1.distanceTo(e2) < SAFE_MERGE_EPS;
    const matchFlip = s1.distanceTo(e2) < SAFE_MERGE_EPS && e1.distanceTo(s2) < SAFE_MERGE_EPS;
    return { match: matchDirect || matchFlip, isFlipped: matchFlip };
  };

  // 2. MERGE DETECTION: Identify overlapping walls
  for (let i = 0; i < walls.length; i++) {
    let w1 = walls[i];
    if (!(w1 instanceof StandardWallModel) || toDelete.has(w1.uuid)) continue;

    for (let j = i + 1; j < walls.length; j++) {
      const w2 = walls[j];
      if (!(w2 instanceof StandardWallModel) || toDelete.has(w2.uuid)) continue;

      const { match, isFlipped } = areWallsIdentical(w1, w2);
      if (!match) continue;

      let survivor = w1 as StandardWallModel;
      let victim = w2 as StandardWallModel;
      let swapped = false;

      if (getWeightedScore(victim) > getWeightedScore(survivor)) {
        survivor = w2 as StandardWallModel;
        victim = w1 as StandardWallModel;
        swapped = true;
        if (survivor === building.walls?.[j]) {
          survivor = cloneStandardWallDeep(survivor);
          walls[j] = survivor;
          anyCloned = true;
        }
      } else {
        if (survivor === building.walls?.[i]) {
          survivor = cloneStandardWallDeep(survivor);
          walls[i] = survivor;
          anyCloned = true;
          w1 = survivor;
        }
      }

      const targetStart = isFlipped ? survivor.endPoint : survivor.startPoint;
      const targetEnd = isFlipped ? survivor.startPoint : survivor.endPoint;

      for (let k = 0; k < walls.length; k++) {
        let w = walls[k];
        if (w instanceof StandardWallModel && w.uuid !== victim.uuid && w.uuid !== survivor.uuid) {
          let modified = false;
          const newStart = w.startPoint.clone();
          const newEnd = w.endPoint.clone();

          if (w.startPoint.distanceTo(victim.startPoint) < 0.1) {
            newStart.copy(targetStart);
            modified = true;
          } else if (w.startPoint.distanceTo(victim.endPoint) < 0.1) {
            newStart.copy(targetEnd);
            modified = true;
          }

          if (w.endPoint.distanceTo(victim.startPoint) < 0.1) {
            newEnd.copy(targetStart);
            modified = true;
          } else if (w.endPoint.distanceTo(victim.endPoint) < 0.1) {
            newEnd.copy(targetEnd);
            modified = true;
          }

          if (modified) {
            if (!anyCloned && w === building.walls?.[k]) {
              w = cloneStandardWallDeep(w);
              walls[k] = w;
              anyCloned = true;
            }
            w.startPoint.copy(newStart);
            w.endPoint.copy(newEnd);
            if (typeof (w as any).updateWallLengths === 'function') (w as any).updateWallLengths();
          }
        }
      }

      toDelete.add(victim.uuid);
      replacements.set(victim.uuid, survivor);

      if (!survivor.attributes?.length && victim.attributes?.length) survivor.attributes = [...victim.attributes];
      const flip = isFlipped;
      if (!survivor.doors?.length && victim.doors?.length) {
        survivor.doors = victim.doors.map((d) => {
          const nd = clone(d);
          if (flip && typeof (nd as any).t === 'number') (nd as any).t = 1 - (nd as any).t;
          return nd;
        });
      }
      if (!survivor.windows?.length && victim.windows?.length) {
        survivor.windows = victim.windows.map((w) => {
          const nw = clone(w);
          if (flip && typeof (nw as any).t === 'number') (nw as any).t = 1 - (nw as any).t;
          return nw;
        });
      }
      (['A', 'B'] as const).forEach((side) => {
        const victimRads = victim.sides?.[side]?.radiators;
        if (victimRads?.length) {
          const targetSide = flip ? (side === 'A' ? 'B' : 'A') : side;
          if (!survivor.sides) survivor.sides = {};
          if (!survivor.sides[targetSide]) survivor.sides[targetSide] = { radiators: [] };
          if (!survivor.sides[targetSide]!.radiators.length) {
            survivor.sides[targetSide]!.radiators = victimRads.map((r) => clone(r));
          }
        }
      });
      if (swapped) break;
    }
  }

  if (toDelete.size === 0 && !anyCloned) return building;

  const finalWalls = walls.filter((w) => !toDelete.has(w.uuid));

  // 3. FINAL HEALING: Snap loose endpoints left behind by deletions/merges
  finalWalls.forEach((other) => {
    if (!(other instanceof StandardWallModel)) return;
    finalWalls.forEach((target) => {
      if (other === target) return;
      let modified = false;
      [target.startPoint, target.endPoint].forEach((pt) => {
        if (other.startPoint.distanceTo(pt) < SAFE_SNAP_TOL) {
          other.startPoint.copy(pt);
          modified = true;
        }
        if (other.endPoint.distanceTo(pt) < SAFE_SNAP_TOL) {
          other.endPoint.copy(pt);
          modified = true;
        }
      });
      if (modified) {
        other.startPoint.z = 0;
        other.endPoint.z = 0;
        if (typeof (other as any).updateWallLengths === 'function') (other as any).updateWallLengths();
      }
    });
  });

  const nextBuilding: IBuildingProviderContext = { ...building, walls: finalWalls };
  return rehydrateAllWallRefs(nextBuilding, finalWalls, replacements);
}

// --- DRAG HOOK ---

interface IDragSnapshot {
  initialStart: THREE.Vector3;
  initialEnd: THREE.Vector3;
  dragOrigin: THREE.Vector3;
  fixedNormal: THREE.Vector3;
  neighborsAtStart: Array<{ index: number; end: EndKind }>;
  neighborsAtEnd: Array<{ index: number; end: EndKind }>;
}

const useWallDragging = (
  raycaster: THREE.Raycaster,
  camera: Camera,
  planeRef: React.RefObject<THREE.Object3D>,
  onWallUpdate: (wallUuid: string, lastWall: IWall) => void,
  wallModel: IWall,
  walls: IWall[],
) => {
  const [wallPerpendicularDrag, _setWallPerpendicularDrag] = useState(false);
  const [draggingPosition, _setDraggingPosition] = useState<EDraggablePointType>();
  const { setIsDragging } = useDraggingContext();
  const { isSnappingEnabled } = useMultiSelectToggleContext();
  const { setBuilding } = useBuilding();
  const { interactionMode } = useToolbarContext();

  const wallUuid = wallModel.uuid;

  const derivedIndex = React.useMemo(() => walls.findIndex((w) => w.uuid === wallUuid), [walls, wallUuid]);

  const dragEnabled = interactionMode !== EInteractionMode.DRAW && derivedIndex !== -1;
  const { gl } = useThree();
  const dragSnapshotRef = useRef<IDragSnapshot | null>(null);
  const dragCleanupAbortRef = useRef<AbortController | null>(null);

  const dragStateRef = useRef<{ active: boolean; type: 'perp' | 'point' | null; draggingPosition: EDraggablePointType | undefined }>({
    active: false,
    type: null,
    draggingPosition: undefined,
  });
  const latestContextRef = useRef({ walls, derivedIndex, isSnappingEnabled, onWallUpdate, dragEnabled, wallPerpendicularDrag, draggingPosition });

  useEffect(() => {
    latestContextRef.current = { walls, derivedIndex, isSnappingEnabled, onWallUpdate, dragEnabled, wallPerpendicularDrag, draggingPosition };
  }, [walls, derivedIndex, isSnappingEnabled, onWallUpdate, dragEnabled, wallPerpendicularDrag, draggingPosition]);

  const handleWallPerpendicularDrag = useCallback((worldPos: THREE.Vector3): void => {
    const { walls, derivedIndex, onWallUpdate } = latestContextRef.current;
    try {
      const currentWall = walls[derivedIndex];
      if (!(currentWall instanceof StandardWallModel)) return;

      if (!dragSnapshotRef.current) {
        const p1 = currentWall.startPoint.clone();
        const p2 = currentWall.endPoint.clone();
        const ab = new THREE.Vector3().subVectors(p2, p1);
        if (ab.lengthSq() < EPS * EPS) return;

        const n = new THREE.Vector3(-ab.y, ab.x, 0).normalize();

        dragSnapshotRef.current = {
          initialStart: p1,
          initialEnd: p2,
          dragOrigin: worldPos.clone(),
          fixedNormal: n,
          neighborsAtStart: getConnectedWallsAtPoint(walls, p1, derivedIndex),
          neighborsAtEnd: getConnectedWallsAtPoint(walls, p2, derivedIndex),
        };
      }

      const { initialStart, initialEnd, dragOrigin, fixedNormal, neighborsAtStart, neighborsAtEnd } = dragSnapshotRef.current;

      const mouseDelta = new THREE.Vector3().subVectors(worldPos, dragOrigin);
      const slideDist = mouseDelta.dot(fixedNormal);
      const disp = fixedNormal.clone().multiplyScalar(slideDist);
      const sharedStart = initialStart.clone().add(disp).setZ(0);
      const sharedEnd = initialEnd.clone().add(disp).setZ(0);

      if (!isValidSegmentFromPoints(sharedStart, sharedEnd)) return;

      const mainWall = walls[derivedIndex] as StandardWallModel;
      setWallEndPreserveRef(mainWall, 'start', sharedStart);
      setWallEndPreserveRef(mainWall, 'end', sharedEnd);
      onWallUpdate(mainWall.uuid, mainWall);

      const updatedIndices = new Set<number>([derivedIndex]);

      neighborsAtStart.forEach(({ index: wi, end }) => {
        if (updatedIndices.has(wi)) return;
        updatedIndices.add(wi);
        const w = walls[wi] as StandardWallModel;
        const other = end === 'start' ? w.endPoint : w.startPoint;
        if (!isValidSegmentFromPoints(sharedStart, other)) return;
        setWallEndPreserveRef(w, end, sharedStart);
        onWallUpdate(w.uuid, w);
      });

      neighborsAtEnd.forEach(({ index: wi, end }) => {
        if (updatedIndices.has(wi)) return;
        updatedIndices.add(wi);
        const w = walls[wi] as StandardWallModel;
        const other = end === 'start' ? w.endPoint : w.startPoint;
        if (!isValidSegmentFromPoints(sharedEnd, other)) return;
        setWallEndPreserveRef(w, end, sharedEnd);
        onWallUpdate(w.uuid, w);
      });
    } catch (e) {
      console.error(e);
      dragSnapshotRef.current = null;
    }
  }, []);

  const handleWallPointDrag = useCallback((snapped: THREE.Vector3): boolean => {
    const { walls, derivedIndex, onWallUpdate } = latestContextRef.current;
    const draggingPosition = dragStateRef.current.draggingPosition;

    const currentWall = walls[derivedIndex];
    if (!currentWall) return false;

    let didMagneticSnap = false;

    const draggingStart = draggingPosition === EDraggablePointType.START;
    const oldAnchor = draggingStart ? currentWall.startPoint.clone() : currentWall.endPoint.clone();
    const sharedNewPos = snapped.clone().setZ(0);

    const connected = getConnectedWallsAtPoint(walls, oldAnchor, derivedIndex);
    const connectedIndices = new Set(connected.map((c) => c.index));

    let bestDist = OMNI_SNAP_RADIUS;
    let magneticPoint: THREE.Vector3 | null = null;

    walls.forEach((w, i) => {
      if (i === derivedIndex) return;
      if (connectedIndices.has(i)) return;

      const dStart = sharedNewPos.distanceTo(w.startPoint);
      if (dStart < bestDist) {
        bestDist = dStart;
        magneticPoint = w.startPoint.clone();
      }
      const dEnd = sharedNewPos.distanceTo(w.endPoint);
      if (dEnd < bestDist) {
        bestDist = dEnd;
        magneticPoint = w.endPoint.clone();
      }
    });

    const selfOtherEnd = draggingStart ? currentWall.endPoint : currentWall.startPoint;
    const dSelf = sharedNewPos.distanceTo(selfOtherEnd);
    if (dSelf < OMNI_SNAP_RADIUS && dSelf < bestDist) {
      magneticPoint = selfOtherEnd.clone();
    }

    if (magneticPoint) {
      sharedNewPos.copy(magneticPoint);
      didMagneticSnap = true;
    }

    if (currentWall.type === EWall.PARTITION) {
      const mainWall: any = currentWall;
      if (draggingStart) mainWall.startPoint.copy(sharedNewPos);
      else mainWall.endPoint.copy(sharedNewPos);
      if (typeof mainWall.updateWallLengths === 'function') mainWall.updateWallLengths();
      onWallUpdate(mainWall.uuid, mainWall);
      return didMagneticSnap;
    }

    if (!(currentWall instanceof StandardWallModel)) return false;

    const mainStd = currentWall as StandardWallModel;
    setWallEndPreserveRef(mainStd, draggingStart ? 'start' : 'end', sharedNewPos);
    onWallUpdate(mainStd.uuid, mainStd);

    connected.forEach(({ index: wi, end }) => {
      const neighbor = walls[wi];
      if (neighbor instanceof StandardWallModel) {
        setWallEndPreserveRef(neighbor, end, sharedNewPos);
        onWallUpdate(neighbor.uuid, neighbor);
      }
    });

    return didMagneticSnap;
  }, []);

  const handleGlobalPointerUp = useCallback(() => {
    const wasDragging = dragStateRef.current.active;

    dragCleanupAbortRef.current?.abort();
    dragCleanupAbortRef.current = null;
    dragSnapshotRef.current = null;
    dragStateRef.current = { active: false, type: null, draggingPosition: undefined };

    _setWallPerpendicularDrag(false);
    _setDraggingPosition(undefined);
    setIsDragging(false);

    if (wasDragging && latestContextRef.current.dragEnabled) {
      setBuilding((prev) => consolidateBuildingWalls(prev));
    }
  }, [setIsDragging, setBuilding]);

  const armGlobalDragCleanup = useCallback(() => {
    dragCleanupAbortRef.current?.abort();

    const controller = new AbortController();
    dragCleanupAbortRef.current = controller;
    const options: AddEventListenerOptions = {
      capture: true,
      signal: controller.signal,
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleGlobalPointerUp();
      }
    };

    window.addEventListener('pointerup', handleGlobalPointerUp, options);
    window.addEventListener('pointercancel', handleGlobalPointerUp, options);
    window.addEventListener('blur', handleGlobalPointerUp, { signal: controller.signal });
    document.addEventListener('visibilitychange', handleVisibilityChange, { signal: controller.signal });
  }, [handleGlobalPointerUp]);

  useEffect(() => () => {
    dragCleanupAbortRef.current?.abort();
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent): void => {
      if (!dragStateRef.current.active) return;

      if (event.buttons === 0) {
        handleGlobalPointerUp();
        return;
      }

      const { isSnappingEnabled, walls } = latestContextRef.current;

      try {
        const rect = gl.domElement.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        const pointer = new THREE.Vector2(x, y);

        raycaster.setFromCamera(pointer, camera);
        if (!planeRef.current) return;

        const intersects = raycaster.intersectObject(planeRef.current);
        if (intersects.length === 0) return;

        const world = intersects[0].point.clone();
        world.z = 0;

        let snappedPoint = world;

        if (isSnappingEnabled) {
          const { snappedPoint: snap } = getSnappedPoint(world.x, world.y, walls, 0.5);
          snappedPoint = snap;
        }

        if (dragStateRef.current.type === 'perp') {
          handleWallPerpendicularDrag(snappedPoint);
          return;
        }

        if (dragStateRef.current.type === 'point') {
          const didMagneticSnap = handleWallPointDrag(snappedPoint);
          if (didMagneticSnap) {
            handleGlobalPointerUp();
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    [camera, raycaster, gl, handleGlobalPointerUp, handleWallPointDrag, handleWallPerpendicularDrag, planeRef],
  );

  useEffect(() => {
    if (!dragEnabled) return;

    const isDraggingSomething = wallPerpendicularDrag || draggingPosition !== undefined;
    if (!isDraggingSomething) return;

    globalEventManager.registerPointerMove(handlePointerMove);
    globalEventManager.registerPointerUp(handleGlobalPointerUp);
    window.addEventListener('pointercancel', handleGlobalPointerUp);

    return () => {
      globalEventManager.unregisterPointerMove(handlePointerMove);
      globalEventManager.unregisterPointerUp(handleGlobalPointerUp);
      window.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, [dragEnabled, handlePointerMove, handleGlobalPointerUp, wallPerpendicularDrag, draggingPosition]);

  const setWallPerpendicularDragWrapper = useCallback(
    (isPerpendicular: boolean) => {
      if (!dragEnabled) return;

      if (isPerpendicular && dragStateRef.current.active && dragStateRef.current.type === 'point') {
        return;
      }

      if (isPerpendicular) {
        dragSnapshotRef.current = null;
        dragStateRef.current = { active: true, type: 'perp', draggingPosition: undefined };
        armGlobalDragCleanup();

        if (draggingPosition === undefined) _setWallPerpendicularDrag(true);

        setIsDragging(true);
      } else {
        if (dragStateRef.current.type !== 'point') {
          dragStateRef.current = { active: false, type: null, draggingPosition: undefined };
          _setWallPerpendicularDrag(false);
          dragSnapshotRef.current = null;
          setIsDragging(false);
        }
      }
    },
    [armGlobalDragCleanup, dragEnabled, draggingPosition, setIsDragging],
  );

  const setDraggingPositionWrapper = useCallback(
    (positionType: EDraggablePointType | undefined) => {
      if (!dragEnabled) return;

      dragSnapshotRef.current = null;

      if (positionType !== undefined) {
        dragStateRef.current = { active: true, type: 'point', draggingPosition: positionType };
        armGlobalDragCleanup();
        _setWallPerpendicularDrag(false);
        _setDraggingPosition(positionType);
        setIsDragging(true);
      } else {
        dragStateRef.current = { active: false, type: null, draggingPosition: undefined };
        _setDraggingPosition(undefined);
        setIsDragging(false);
      }
    },
    [armGlobalDragCleanup, dragEnabled, setIsDragging],
  );

  return {
    wallPerpendicularDrag,
    setWallPerpendicularDrag: setWallPerpendicularDragWrapper,
    handlePointerMove,
    handleGlobalPointerUp,
    setDraggingPosition: setDraggingPositionWrapper,
    dragEnabled,
  };
};

export default useWallDragging;
